<template>
  <AppLink
    v-if="to"
    v-bind="$attrs"
    :class="classes"
    :ga-event="gaEvent"
    :open-in-another-tab="openInAnotherTab"
    :replace="replace"
    :style="styles"
    :to="to"
    v-on="$listeners"
  >
    <slot />
  </AppLink>
  <button v-else v-bind="$attrs" :class="classes" :style="styles" :type="buttonType" v-on="$listeners" @click="onClick">
    <slot />
  </button>
</template>

<script>
import { sendGaEvent } from '@/helpers/ga/gaSend';
import GaEvent from '@/helpers/ga/events/GaEvent';
import AppLink from '@/components/AppLink.vue';

const BTN_BACKGROUND_MAP = {
  primary: 'primary',
  primary_outline: 'primary_outline',
  none: 'none',
};

export default {
  name: 'AppButton',
  components: { AppLink },
  inheritAttrs: false,
  props: {
    background: {
      type: String,
      default: BTN_BACKGROUND_MAP.primary,
      validator(value) {
        return value
          ? [BTN_BACKGROUND_MAP.primary, BTN_BACKGROUND_MAP.primary_outline, BTN_BACKGROUND_MAP.none].includes(value)
          : true;
      },
    },
    borderWidth: {
      type: String,
      default: '1px',
    },
    centered: {
      type: Boolean,
      default: false,
    },
    fontSize: {
      type: String,
      default: null,
    },
    gaEvent: {
      type: Object,
      default: null,
      validator: (value) => {
        if (value === null) {
          return true;
        }
        return typeof value === 'object' && value.name && value.params !== null && typeof value.params === 'object';
      },
    },
    inverse: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: null,
    },
    openInAnotherTab: {
      type: Boolean,
      default: false,
    },
    replace: {
      type: Boolean,
      default: false,
    },
    to: {
      type: [Object, String],
      default: null,
    },
    type: {
      type: String,
      default: null,
      validator: (val) => val === 'button' || val === 'submit',
    },
  },

  computed: {
    buttonBackgroundClass() {
      return (BTN_BACKGROUND_MAP[this.background] || BTN_BACKGROUND_MAP.primary).replace('_', '-');
    },
    buttonType() {
      return !this.to ? this.type || 'button' : null;
    },
    classes() {
      return [
        'app-button',
        `app-button--${this.buttonBackgroundClass}`,
        {
          'app-button--centered': this.centered,
          'app-button--inverse': this.inverse,
        },
      ];
    },
    styles() {
      return {
        '--app-btn-border-width': `${this.borderWidth || '1px'}`,
        '--app-btn-font-size': this.fontSize || undefined,
        '--app-btn-max-width': `${this.maxWidth || 'none'}`,
      };
    },
  },

  methods: {
    onClick(e) {
      if (this.gaEvent) {
        const gaEvent = GaEvent.fromPlainObject(this.gaEvent);
        if (!gaEvent.hasCtaName()) {
          gaEvent.addCtaName(e.target.innerText);
        }
        sendGaEvent(gaEvent);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;
@use '@/assets/scss/mixins';

.app-button {
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  font-size: var(--app-btn-font-size, 16px);
  font-weight: 500;
  letter-spacing: 1px;
  line-height: 1;
  color: vars.$c-total-black;
  text-decoration: none;
  transition: all 0.1s linear;

  @media (min-width: vars.$mobile-break-point + 1) {
    max-width: var(--app-btn-max-width);
  }

  &:not(&--none) {
    width: 100%;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;

    @media (min-width: vars.$tablet-break-point) {
      @include mixins.font-with-spacing(var(--app-btn-font-size, 18px), 3.24);
    }
  }

  &:not([disabled]):hover {
    opacity: 0.75;
  }

  &--centered {
    margin-left: auto;
    margin-right: auto;
  }

  &--primary {
    color: vars.$c-total-white;
    background-color: vars.$c-total-black;

    &[disabled] {
      background-color: vars.$c-gray-a2;
    }

    &:not([disabled]):hover,
    &:not([disabled]):focus {
      color: vars.$c-total-white !important;
    }
  }

  &--inverse,
  &--primary-outline,
  &--none {
    &[disabled] {
      opacity: 0.4;
    }
  }

  &--inverse,
  &--primary-outline {
    color: vars.$c-total-black;
    background-color: vars.$c-total-white;
    border: var(--app-btn-border-width) solid vars.$c-total-black;

    &:not([disabled]):hover {
      color: vars.$c-total-white;
      background-color: vars.$c-total-black;
    }
  }
}
</style>
